/**
 * RU strings for the device-enrollment portal (Amendment 02 Part A). Wording is
 * operator-facing default copy; the exact confirmation/blocked text is described
 * as operator-authored in the spec, so these are sensible defaults. Enum labels
 * are keyed by the EXACT backend wire value (identity_form / origin / state).
 */
const ru = {
  brand: 'Интернет',

  login: {
    title: 'Регистрация устройства',
    subtitle: 'Войдите под своей учётной записью, чтобы зарегистрировать это устройство и получить доступ в интернет.',
    loginLabel: 'Логин',
    loginRequired: 'Введите логин',
    passwordLabel: 'Пароль',
    passwordRequired: 'Введите пароль',
    submit: 'Войти',
    invalidCredentials: 'Неверный логин или пароль.',
    // 429 rate_limited — the login endpoint is throttled per device address, so a
    // subscriber who mistyped a few times is told to wait rather than shown a
    // failure he cannot explain.
    tooManyAttempts: 'Слишком много попыток входа. Подождите минуту и попробуйте снова.',
    genericError: 'Не удалось выполнить вход. Попробуйте ещё раз.',
    networkError: 'Нет связи с сервером. Проверьте подключение и попробуйте снова.',
  },

  connection: {
    title: 'Подключаемое устройство',
    // ⚠ THE FIRST ROW OF THE PANEL, AND THE ONE THAT IS ALWAYS THERE (customer,
    // 2026-08-22). The operator is on the telephone with the subscriber and needs an
    // address he can act on; the subscriber is asked to open this page and read it
    // out. It is shown before any login and whether or not the port was detected.
    clientIp: 'Ваш IP-адрес',
    // The caveat, in one sentence: a subscriber behind his own router sees the
    // ROUTER's external address, not the one his laptop shows in its settings. That
    // is still the address billing holds, so it is the right one to read out — but he
    // will otherwise think the page is wrong and start arguing with the operator.
    ipNote:
      'Это адрес вашего подключения — если стоит свой роутер, он отличается от адреса на компьютере. Назовите его оператору.',
    mac: 'MAC-адрес',
    vlan: 'VLAN',
    opt82Circuit: 'Option 82 (Circuit)',
    opt82Remote: 'Option 82 (Remote)',
    trap: 'Коммутатор / порт',
    // Shown as a muted hint (not an error) when the identity read-out is
    // unavailable: the portal determines the device by the connection itself, so
    // the subscriber has nothing to do about it and must NOT be asked for an address.
    none: 'Подключаемое устройство пока не определено. Просто войдите — оно определится автоматически по вашему подключению.',
  },

  register: {
    working: 'Регистрируем ваше устройство…',
    // 404 captive.context_not_found — no `issued` captive lease for this address:
    // the lease expired, or the device is not actually in a captive session.
    //
    // ~~'Для вашего подключения не найдена активная сессия захвата: скорее всего,
    // истёк срок аренды адреса. Отключитесь от сети и подключитесь заново (или
    // перезагрузите устройство), затем откройте портал ещё раз.'~~ STRUCK IN PLACE
    // 2026-09-01 (D408). Production served that sentence 1582 times in eighteen
    // hours, overwhelmingly to subscribers the month boundary had financially
    // suspended: a suspended subscriber sits in the walled garden holding his
    // ordinary PUBLIC address, which has no captive lease at all, so this is the
    // answer he gets — and being told to reboot his router is advice that cannot
    // possibly help him, because his problem is that he owes money. That population
    // no longer reaches this string at all (EnrollmentFlow's BLOCKING_STATES routes
    // them to the replenishment screen instead), and the text is struck rather than
    // deleted because its two defects are the ones a rewrite invites back: it
    // ASSERTED A CAUSE it did not know («скорее всего, истёк срок аренды») and it
    // offered ONE remedy as though it were the fix.
    //
    // ~~'Не удалось зарегистрировать это устройство: ваше подключение сейчас не в
    // режиме регистрации. Попробуйте переподключиться к сети и открыть портал ещё
    // раз. Если это не помогло — обратитесь в техническую поддержку: оператор
    // определит ваше подключение по вашей учётной записи и подскажет, что делать.'~~
    // STRUCK IN PLACE the same day it was written, when the BACKEND half of D408
    // landed and narrowed the code underneath it. `captive.context_not_found` no
    // longer means «no captive lease» — every address inside a pool this ISP declared
    // now answers `200` — it means THE ADDRESS IS OUTSIDE EVERY DECLARED POOL, i.e.
    // the caller is not on this ISP's network at all. Against that narrower meaning
    // the text above said two things it no longer knows: «переподключиться к сети»
    // helps nobody who is on mobile data or a foreign Wi-Fi (reconnecting to THAT
    // network changes nothing), and an operator canNOT «определить ваше подключение»
    // from an address that is not ours. It is struck rather than deleted because it
    // is the exact hazard this project keeps re-learning: copy that was true for one
    // condition, surviving the condition's redefinition.
    //
    // The new text states the one thing the 404 now proves and the one action that
    // follows from it, and it keeps a support route for the case where the subscriber
    // is right and a pool declaration is wrong — which is an operator's problem, not
    // his. No jargon: «сессия захвата» is a phrase from the RADIUS layer.
    noLease:
      'Эта страница открыта не через сеть провайдера: ваш адрес нам не принадлежит — ' +
      'так бывает при выходе через мобильный интернет, чужой Wi-Fi или VPN. ' +
      'Зарегистрировать можно только то устройство, с которого вы пользуетесь нашим интернетом: ' +
      'подключитесь к нашей сети и откройте портал ещё раз. ' +
      'Если вы уверены, что подключены к нашей сети, обратитесь в техническую поддержку.',
    // 403 captive.source_ip_mismatch — the address in the portal link is not the
    // address this device connects from; registration binds only to your own.
    sourceIpMismatch:
      'Адрес, указанный в ссылке портала, не совпадает с адресом этого устройства. Зарегистрировать можно только то устройство, с которого открыт портал: откройте портал без параметров в адресной строке — адрес определится автоматически.',
    // 400 captive.invalid_source_ip — the address could not be parsed at all.
    invalidSourceIp:
      'Не удалось определить адрес подключения. Откройте портал без параметров в адресной строке — адрес определится автоматически.',
    // 401 captive.unauthorized on a state-changing call — the short-lived portal
    // token expired; the flow returns to the login screen with this message.
    sessionExpired: 'Время сессии истекло. Войдите ещё раз.',
    // 409 devices.mac_bound_to_another_account — this device is already registered
    // to a DIFFERENT account (reported from production 2026-08-17).
    //
    // ⚠ IT MUST NOT SAY «попробуйте ещё раз», which is what it said before: the
    // refusal is PERMANENT until somebody unbinds the device, so a retry can only
    // ever fail again. That is the same shape as the «Состояние счёта временно
    // недоступно» defect fixed the same morning — a screen promising that waiting
    // helps, on a condition waiting cannot change.
    //
    // ⚠ AND IT MUST NOT NAME THE OTHER ACCOUNT. The core deliberately answers this
    // one code with NO details (D330, internal/captive.writeDevErr): the body would
    // otherwise carry another subscriber's account id to the walled garden, which
    // anyone holding an address can reach. Take-over is a staff action, so the only
    // honest route from here is support.
    boundElsewhere:
      'Это устройство уже зарегистрировано на другой учётной записи. ' +
      'Повторная попытка не поможет — обратитесь в техническую поддержку, ' +
      'чтобы перенести устройство на эту учётную запись.',
    // 5xx / captive.not_wired.
    serverError: 'Сервер временно недоступен. Повторите попытку через минуту.',
    networkError: 'Нет связи с сервером. Проверьте подключение и попробуйте снова.',
    genericError: 'Не удалось зарегистрировать устройство. Попробуйте ещё раз.',
    retry: 'Повторить',

    // ---- The STATIC-ADDRESS choice at registration (customer request 2026-08-17) ----
    //
    // ~~⚠ NOTHING IN THIS APP CAN PRODUCE THESE FOUR TODAY … the portal has no
    // authorised route that lists the account's unbound static addresses, so it never
    // sends one and the core never runs the binding these refusals come from.~~
    // STRUCK IN PLACE 2026-08-18: `GET /captive/static-addresses` was built and
    // AddressChoiceScreen offers the choice, so every one of these is now REACHABLE by
    // an ordinary subscriber — most sharply `staticNotYours`, which is what a screen
    // left open while an operator re-assigned the address produces.
    //
    // The copy exists because they are DOCUMENTED answers of `POST /captive/register`
    // (api/openapi.yaml), and the fallback they would otherwise land on is the wrong
    // one for every one of them: «Попробуйте ещё раз» promises that retrying the SAME
    // request helps, which is false for all four — the identical defect fixed for
    // `devices.mac_bound_to_another_account` the same morning.
    //
    // ⚠ AND NONE OF THEM MAY NAME AN ADDRESS, A DEVICE OR AN ACCOUNT. The core strips
    // `details` from these on the walled-garden listener precisely because they carry
    // another subscriber's account id (D330); copy that invented specifics would be
    // either wrong or an argument for handing the details back.
    //
    // All four also state that the device was NOT registered — true, because the bind
    // runs inside the registration's own transaction and its failure rolls the whole
    // registration back (devices.bindChosenStatic).

    // 409 ippool.static_not_of_account / 404 ippool.lease_not_found — the lease is not
    // a `static` of THIS account (released back to the pool, or reassigned).
    staticNotYours:
      'Выбранный статический адрес больше не закреплён за вашей учётной записью, ' +
      'поэтому устройство не зарегистрировано. Обновите страницу и выберите адрес заново ' +
      'или зарегистрируйте устройство без выбора адреса.',
    // 409 ippool.static_already_bound — this account's static, but already on ANOTHER
    // of the subscriber's own devices (the account matched, or the code would have
    // been static_not_of_account).
    staticAlreadyBound:
      'Выбранный статический адрес уже закреплён за другим вашим устройством, ' +
      'поэтому это устройство не зарегистрировано. Выберите другой адрес — ' +
      'или сначала отключите устройство, за которым этот адрес закреплён, ' +
      'и его адрес снова станет доступен.',
    // 409 ippool.device_static_conflict — one device may hold only one static per pool.
    staticDeviceConflict:
      'За этим устройством уже закреплён статический адрес из того же диапазона, ' +
      'а второй адрес из того же диапазона закрепить нельзя. ' +
      'Зарегистрируйте устройство без выбора адреса или обратитесь в техническую поддержку.',
    // 503 devices.static_address_unavailable — the build has no address binder wired.
    staticUnavailable:
      'Выбор статического адреса при регистрации сейчас недоступен, ' +
      'поэтому устройство не зарегистрировано. Зарегистрируйте его без выбора адреса — ' +
      'статический адрес закрепит оператор.',
  },

  // The STATIC-ADDRESS choice at registration (customer request 2026-08-17;
  // subscriber half 2026-08-18). The screen is shown ONLY to an account that holds at
  // least one assigned, unused static address — for everybody else there is no screen
  // and no control at all, so none of this copy is ever read.
  //
  // ⚠ NOTHING HERE NAMES A POOL, A RANGE, A DEVICE OR AN ACCOUNT, and none of it is
  // interpolated with anything but the subscriber's OWN address, which he is being
  // asked to choose. The server sends two fields for exactly that reason.
  addressChoice: {
    title: 'Адрес для этого устройства',
    subtitle:
      'Для вашей учётной записи зарезервированы постоянные IP-адреса. ' +
      'Можно закрепить один из них за этим устройством — или оставить обычный, ' +
      'который выдаётся автоматически.',
    // The DEFAULT, and it is first in the list on purpose: a subscriber who did not
    // ask for a static address must not receive one by pressing «Продолжить».
    dynamic: 'Обычный адрес (по умолчанию)',
    dynamicHint: 'Устройство получит адрес автоматически — так работает большинство подключений.',
    staticHint: 'Этот адрес будет закреплён за устройством.',
    // A single free address is still an explicit choice — NOT auto-selected.
    submit: 'Продолжить',
  },

  choice: {
    title: 'Достигнут лимит устройств',
    subtitle: 'На вашей учётной записи уже зарегистрировано максимальное число устройств. Выберите устройство, которое нужно отключить, чтобы зарегистрировать текущее.',
    limit: 'Лимит устройств: {{limit}}',
    selectHint: 'Выберите устройство из списка',
    confirm: 'Отключить и зарегистрировать',
    confirmError: 'Не удалось завершить замену устройства. Попробуйте ещё раз.',
    identity: 'Идентификатор',
    registered: 'Зарегистрировано',
    lastOnline: 'Был(а) в сети',
    never: '—',
  },

  confirmation: {
    title: 'Устройство зарегистрировано',
    success: 'Это устройство привязано к вашей учётной записи.',
    waitTitle: 'Доступ откроется автоматически',
    wait: 'Ничего делать не нужно: соединение будет переустановлено, устройство получит обычный адрес и полный доступ в интернет — как правило, за несколько секунд. Если этого не произошло, доступ откроется при следующем обновлении адреса, не позже чем через минуту.',
    deviceLabel: 'Зарегистрированное устройство',
    // Variant shown right after a successful self-activation: the core drops the
    // captive session, so the device asks for a new address immediately.
    activatedTitle: 'Услуга активирована',
    activatedSuccess: 'Ваша учётная запись активирована, доступ в интернет уже открывается.',
    activatedWait: 'Ничего делать не нужно: соединение будет переустановлено, устройство получит обычный адрес и полный доступ в интернет — как правило, за несколько секунд, и не позже чем через минуту.',
    // D408 — the block was lifted (a payment landed, «Обновить» saw it) on a session
    // in which the device registration never succeeded. The headline may not claim a
    // binding nobody wrote, so it names what IS known — the account works again —
    // and the note carries the one instruction that recovers an unbound device.
    unconfirmedTitle: 'Доступ восстанавливается',
    unconfirmedSuccess: 'Ваша учётная запись снова активна.',
    unconfirmedWait:
      'Как правило, доступ открывается сам за несколько секунд. ' +
      'Если через минуту интернет не заработал, откройте эту страницу ещё раз и войдите — ' +
      'устройство зарегистрируется автоматически.',
    // D408 (backend half) — `outcome: "no_device_identity"` on a SERVICEABLE account:
    // he is on an address this ISP owns that no captive lease backs, so the call bound
    // nothing and needed to bind nothing. Nothing failed, nothing changed, and there
    // is no retry worth offering — the same request from the same address answers the
    // same thing for as long as he holds it.
    //
    // ⚠ IT MAY NOT SAY «доступ откроется» (the core's own constant block forbids it:
    // this call opened nothing) AND IT MAY NOT SAY «устройство зарегистрировано»
    // (nothing was written). What it CAN say is what the 200 itself proves: the
    // account is active, and the connection is one of ours holding an ordinary
    // non-garden address. The caveat names the route out for the subscriber whose
    // experience contradicts all that — and the operator really can find him, because
    // the address IS the ISP's, which is the difference from `noLease` above.
    noActionTitle: 'Регистрация не требуется',
    noActionSuccess:
      'Ваша учётная запись активна, а это подключение уже получило обычный адрес — ' +
      'регистрировать устройство на этой странице не нужно.',
    noActionNoteTitle: 'Ничего делать не нужно',
    noActionWait:
      'Если интернет всё-таки не работает, обратитесь в техническую поддержку: ' +
      'ваше подключение видно оператору, и он разберётся вместе с вами.',
  },

  // Self-activation of a brand-new (never activated) account. All amounts come from
  // the core as decimal strings and are shown through formatRubles().
  activation: {
    title: 'Активация услуги',
    subtitle: 'Устройство зарегистрировано. Осталось активировать услугу — после этого интернет заработает сразу.',
    deviceRegistered: 'Это устройство уже зарегистрировано на вашей учётной записи, регистрировать его заново не нужно.',
    // D408 (backend half) — the same screen for a never-activated subscriber whose
    // registration call bound nothing (`outcome: "no_device_identity"`: an ISP address
    // with no captive lease behind it, e.g. a static one assigned before activation).
    // The activation is still exactly what he must do, so only the two device
    // sentences change. They are SWAPPED rather than dropped: a screen that says
    // nothing at all about the device is what makes a subscriber re-register in a loop.
    subtitleUnconfirmed: 'Осталось активировать услугу — после этого интернет заработает сразу.',
    deviceUnconfirmed:
      'Регистрировать это устройство на этой странице сейчас не нужно — на вашем подключении ' +
      'это не требуется. Сначала активируйте услугу; если после этого интернет не заработает, ' +
      'откройте эту страницу ещё раз и войдите.',
    detailsLabel: 'Условия активации',
    tariff: 'Тариф',
    charge: 'Списание при активации',
    chargeNone: 'Без списания',
    balance: 'Баланс',
    required: 'Нужно на счету',
    shortfall: 'Не хватает',
    // can_activate === true
    ready: 'На счету достаточно средств. Нажмите «Активировать» — доступ откроется сразу.',
    activate: 'Активировать',
    error: 'Не удалось активировать услугу. Попробуйте ещё раз.',
    // 409 captive.insufficient_balance — the shortfall comes from the error details.
    insufficientBalance: 'На счету не хватает {{amount}}. Пополните счёт и нажмите «Обновить».',
    // 409 captive.activation_not_available
    notAvailable: 'Активацию по этой учётной записи выполнить нельзя. Обратитесь в техническую поддержку.',
    // can_activate === false
    needMoneyTitle: 'Пополните счёт, чтобы активировать',
    needMoney: 'Для активации на счету должно быть {{required}}, сейчас {{balance}}.',
    // Same situation, but nothing is REQUIRED on the account (min 0, no charge) and
    // the balance is simply negative: naming a required "0.00 ₽" next to a shortfall
    // reads as nonsense, so the debt is named instead.
    needMoneyDebt: 'Чтобы активировать услугу, погасите задолженность: не хватает {{shortfall}}.',
    relogin: 'Войти под другой учётной записью',
  },

  // The PLAN CHOICE at activation (customer, 2026-08-16). Shown only when the account
  // was created against a tariff GROUP and therefore has no plan of its own yet.
  planChoice: {
    label: 'Выберите тарифный план',
    labelNamed: 'Выберите тарифный план — {{group}}',
    hint: 'Нажмите на план, чтобы выбрать его. Сменить тариф можно будет позже в личном кабинете.',
    // «Списание сейчас» and «Дальше» are the two figures the customer asked for by
    // name: what pressing the button costs today (the remainder of текущий период) and
    // what the plan costs every period after that.
    chargeNow: 'Списание сейчас',
    cadenceMonthly: 'Далее, в месяц',
    cadenceDaily: 'Далее, в месяц (списывается по дням)',
    cadencePeriod: 'Далее, за период',
    shortfall: 'Не хватает',
    // charge_known === false: nobody could price the plan. NEVER shown as 0,00 ₽.
    priceUnknown: 'уточняйте',
    // choice_required with an empty list — a misconfiguration, not an empty page.
    noneTitle: 'Тарифные планы недоступны',
    noneBody: 'Для вашей учётной записи пока не настроены тарифные планы. Обратитесь в техническую поддержку — мы всё быстро поправим.',
    noneArchived: 'Набор тарифных планов для вашей учётной записи больше не действует. Обратитесь в техническую поддержку — мы подберём тариф.',
    // The activate button is disabled until a plan is picked.
    selectFirst: 'Выберите тарифный план, чтобы продолжить.',
    // 409 captive.tariff_not_selectable — the picked plan was withdrawn meanwhile; the
    // list is re-read under this notice.
    withdrawn: 'Выбранный тарифный план больше недоступен. Список планов обновлён — выберите план ещё раз.',
  },

  // Ways out of a money problem, shared by the activation screen (a shortfall) and
  // the financial-block screen (a debt) — see portal/TopUpRoutes.tsx.
  topup: {
    card: 'Пополнить картой',
    // The card route WORKS from Phase 3 on, so it is described rather than
    // apologised for: what happens next, and where the card data goes. The last
    // sentence is the load-bearing one — the subscriber must know that coming back
    // to this page is the end of the flow, not a step he has to figure out.
    cardHint:
      'Оплата проходит на защищённой странице платёжной системы — данные карты в биллинг не передаются. После оплаты вы вернётесь на эту страницу, и доступ откроется автоматически.',
    // Office/ОСМП are now ALTERNATIVES, not the only way. The wording says so:
    // "тоже можно", after the card block, under a heading that does not claim to be
    // the answer to "как пополнить счёт".
    officeTitle: 'Другие способы пополнения',
    officeHint: 'Счёт также можно пополнить в офисе или через любой терминал ОСМП. Такой платёж зачисляется в течение нескольких минут — после этого нажмите «Обновить».',
    refresh: 'Обновить',
    // 503 captive.topup_unavailable — a calm notice, never an error. It survives
    // Phase 3 because it is still reachable: an installation whose acquirer is not
    // configured yet, or whose PayOnline instance is switched off, answers 503 and
    // the subscriber must be pointed at the routes that do work.
    unavailable: 'Оплата картой пока недоступна. Пополните счёт в офисе или через терминал ОСМП — доступ откроется сразу после зачисления платежа.',
    // 400 captive.invalid_amount — details.max is the provider ceiling.
    invalidAmount: 'Картой можно пополнить не больше чем на {{max}}. Пополните счёт в офисе или через терминал ОСМП.',
    // 502 captive.topup_failed and any unexpected answer.
    error: 'Не удалось перейти к оплате. Пополните счёт в офисе или через терминал ОСМП.',
  },

  // state === 'unknown' — billing could not establish the account state (the core
  // says so explicitly instead of guessing one, which is what used to promise a
  // blocked subscriber that his access was opening). Nothing is claimed about the
  // account here; the screen offers a re-poll and, failing that, support.
  unavailable: {
    title: 'Состояние счёта временно недоступно',
    body: 'Устройство зарегистрировано, но получить состояние вашей учётной записи сейчас не удалось.',
    // D408 (backend half) — the same screen reached with nothing bound. The opening
    // clause is simply removed: this is the one screen whose whole purpose is not to
    // assert what it cannot know, so it may not open with a claim about the device.
    bodyUnconfirmed: 'Получить состояние вашей учётной записи сейчас не удалось.',
    note: 'Нажмите «Обновить» — обычно это занимает несколько секунд. Если состояние не появляется, обратитесь в техническую поддержку.',
    refresh: 'Обновить',
    relogin: 'Войти под другой учётной записью',
  },

  // Administrative block / switched-off account: nothing the subscriber can do
  // himself, so the screen only tells him not to re-register and whom to call.
  support: {
    adminTitle: 'Доступ заблокирован',
    adminBody: 'Установлена административная блокировка, обратитесь в техническую поддержку.',
    disabledTitle: 'Обслуживание приостановлено',
    disabledBody: 'Ваша учётная запись отключена, обратитесь в техническую поддержку.',
    deviceRegistered: 'Это устройство уже зарегистрировано на вашей учётной записи — регистрировать его заново не нужно. Доступ откроется автоматически, как только обслуживание возобновят.',
    // D408 — the same screen, reached from a registration that was REFUSED. The line
    // above would be a lie here, and a lie on this page is what the whole fix is
    // against; this one states the fact, says it is not what he should be acting on,
    // and names the step that recovers the binding once the block is gone.
    deviceUnconfirmed:
      'Зарегистрировать это устройство сейчас не удалось — но начинать нужно не с этого: ' +
      'снять блокировку может только оператор. Когда обслуживание возобновят, ' +
      'откройте эту страницу ещё раз и войдите, если интернет не заработает сам.',
    relogin: 'Войти под другой учётной записью',
  },

  // Financial block — the state the most likely visitor of this portal is in, and
  // therefore one that must offer the same routes out as the activation screen (card
  // stub, office/ОСМП, refresh) rather than a pointer at a Personal Area that does
  // not exist until Phase 7.
  blocked: {
    title: 'Доступ приостановлен',
    body: 'Ваша учётная запись финансово заблокирована. Чтобы восстановить доступ, пополните счёт.',
    note: 'После пополнения доступ откроется автоматически при следующем обновлении адреса — ждать окончания долгой аренды не нужно.',
    balance: 'Баланс',
    debt: 'Задолженность',
    deviceRegistered: 'Это устройство уже зарегистрировано на вашей учётной записи — регистрировать его заново не нужно.',
    // D408 — THE LINE THE PRODUCTION DEFECT ACTUALLY NEEDED. The ~417 accounts the
    // month boundary suspended reach this screen from a `404 captive.context_not_found`,
    // i.e. with nothing bound, so «уже зарегистрировано» would be untrue. What he
    // needs is the order of operations: money first, and a way back if access does not
    // open by itself afterwards.
    deviceUnconfirmed:
      'Зарегистрировать это устройство сейчас не удалось, но сейчас это не главное: ' +
      'сначала нужно пополнить счёт. Если после зачисления платежа интернет не заработает, ' +
      'откройте эту страницу ещё раз и войдите — устройство зарегистрируется автоматически.',
    relogin: 'Войти под другой учётной записью',
  },

  device: {
    identityForm: {
      mac_option82: 'MAC + Option 82',
      mac_trap_port: 'MAC + коммутатор/порт',
      mac_only: 'Только MAC',
    },
    origin: {
      self: 'Самостоятельно',
      manager: 'Через оператора',
    },
  },

  common: {
    retry: 'Повторить',
    back: 'Назад',
    loading: 'Загрузка…',
    // The core's error `message` is the bare machine code (internal/i18n carries no
    // captive.* entries), so the portal NEVER renders it: every documented code maps
    // to copy of its own, and anything unrecognised lands on these three.
    networkError: 'Нет связи с сервером. Проверьте подключение и попробуйте снова.',
    serviceUnavailable: 'Сервер временно недоступен. Повторите попытку через минуту.',
    // 429 rate_limited on any portal call.
    tooManyRequests: 'Слишком много запросов. Подождите минуту и попробуйте снова.',
    unknownError: 'Что-то пошло не так. Попробуйте ещё раз или обратитесь в техническую поддержку.',
  },
} as const;

export default ru;
